import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  accountApiRoutes,
  type CheckoutResponseDto,
  type CountryProductResponseDto,
  type CreateCountryCheckoutDto,
  type EventLog,
  type SubscriptionResponseDto,
} from '@repo/contracts/account-api';

import { withReauth } from '@/lib/auth';

/**
 * Custom base query with JWT auth and automatic token refresh on 401.
 */
const baseQueryWithAuth = withReauth((accessToken) =>
  fetchBaseQuery({
    baseUrl:
      import.meta.env.VITE_ACCOUNT_API_URL ||
      'http://localhost:3014/account-api',
    prepareHeaders: (headers) => {
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
);

export const subscriptionApi = createApi({
  reducerPath: 'subscriptionApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Subscription', 'CountryProducts', 'EventLog'],
  endpoints: (builder) => ({
    getCountryProducts: builder.query<CountryProductResponseDto[], void>({
      query: () => accountApiRoutes.products.listCountries(),
      providesTags: ['CountryProducts'],
    }),
    getTenantSubscription: builder.query<SubscriptionResponseDto, string>({
      query: (tenantId) => accountApiRoutes.subscriptions.get(tenantId),
      providesTags: ['Subscription'],
    }),
    createCountryCheckout: builder.mutation<
      CheckoutResponseDto,
      { tenantSlug: string; dto: CreateCountryCheckoutDto }
    >({
      query: ({ tenantSlug, dto }) => ({
        url: accountApiRoutes.checkout.createCountry(tenantSlug),
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: ['Subscription'],
    }),
    cancelCountrySubscription: builder.mutation<
      void,
      { tenantSlug: string; countrySlug: string }
    >({
      query: ({ tenantSlug, countrySlug }) => ({
        url: accountApiRoutes.checkout.cancelCountry(tenantSlug, countrySlug),
        method: 'POST',
      }),
      invalidatesTags: ['Subscription'],
    }),
    reactivateCountrySubscription: builder.mutation<
      void,
      { tenantSlug: string; countrySlug: string }
    >({
      query: ({ tenantSlug, countrySlug }) => ({
        url: accountApiRoutes.checkout.reactivateCountry(
          tenantSlug,
          countrySlug,
        ),
        method: 'POST',
      }),
      invalidatesTags: ['Subscription'],
    }),
    getSubscriptionEventLogs: builder.query<EventLog[], string>({
      query: (tenantSlug) => accountApiRoutes.eventLogs.list(tenantSlug),
      providesTags: (result, error, tenantSlug) => [
        { type: 'EventLog', id: `SUBSCRIPTION_${tenantSlug}` },
      ],
    }),
  }),
});

export const {
  useGetCountryProductsQuery,
  useGetTenantSubscriptionQuery,
  useCreateCountryCheckoutMutation,
  useCancelCountrySubscriptionMutation,
  useReactivateCountrySubscriptionMutation,
  useGetSubscriptionEventLogsQuery,
} = subscriptionApi;
